import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormMessage,
} from "@common/components/ui/form"
import { Switch } from "@common/components/ui/switch"
import { cn } from "@common/lib/utils"
import type { HTMLInputTypeAttribute, ReactNode } from "react"
import { type FieldValues, type Path, useFormContext } from "react-hook-form"
import { FieldLabel } from "./label"
import { renderPinInput } from "./pin"
import { renderTextInput } from "./text"

const ROW = cn(
  "flex flex-col gap-2 px-4 py-3",
  "sm:flex-row sm:items-center sm:justify-between sm:gap-6",
)

const LABEL = "text-body-medium"

const DESCRIPTION = "text-small-medium text-muted-foreground"

const CONTROL = "flex shrink-0 flex-col gap-1 sm:w-64 sm:items-end"

export function FieldRow({
  label,
  description,
  children,
}: {
  label: ReactNode
  description?: ReactNode
  children: ReactNode
}): React.ReactElement {
  return (
    <div className={ROW}>
      <div className="min-w-0 space-y-0.5">
        {label}
        {description}
      </div>
      <div className={CONTROL}>{children}</div>
    </div>
  )
}

export function FieldRowLabel({
  children,
}: {
  children: ReactNode
}): React.ReactElement {
  return <p className={LABEL}>{children}</p>
}

export function FieldRowDescription({
  className,
  children,
}: {
  className?: string
  children: ReactNode
}): React.ReactElement {
  return <p className={cn(DESCRIPTION, className)}>{children}</p>
}

export function RowTextField<T extends FieldValues>({
  name,
  label,
  description,
  type = "text",
  optional,
}: {
  name: Path<T>
  label: string
  description?: string
  type?: HTMLInputTypeAttribute
  optional?: boolean
}): React.ReactElement {
  const { control } = useFormContext<T>()

  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <FormItem className="space-y-0">
          <FieldRow
            label={
              <FieldLabel label={label} optional={optional} className={LABEL} />
            }
            description={
              description && (
                <FormDescription className={DESCRIPTION}>
                  {description}
                </FormDescription>
              )
            }
          >
            <FormControl>
              {renderTextInput<T>({
                field,
                invalid: !!fieldState.error,
                optional,
                type,
              })}
            </FormControl>
            <FormMessage />
          </FieldRow>
        </FormItem>
      )}
    />
  )
}

export function RowPinField<T extends FieldValues>({
  name,
  label,
  length,
}: {
  name: Path<T>
  label: string
  length: number
}): React.ReactElement {
  const { control } = useFormContext<T>()

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className="space-y-0">
          <FieldRow label={<FieldLabel label={label} className={LABEL} />}>
            <FormControl>{renderPinInput<T>({ field, length })}</FormControl>
            <FormMessage />
          </FieldRow>
        </FormItem>
      )}
    />
  )
}

export function RowSwitchField<T extends FieldValues>({
  name,
  label,
  description,
}: {
  name: Path<T>
  label: string
  description?: string
}): React.ReactElement {
  const { control } = useFormContext<T>()

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className="space-y-0">
          <FieldRow
            label={<FieldLabel label={label} optional className={LABEL} />}
            description={
              description && (
                <FormDescription className={DESCRIPTION}>
                  {description}
                </FormDescription>
              )
            }
          >
            <FormControl>
              <Switch
                ref={field.ref}
                checked={field.value}
                onCheckedChange={field.onChange}
                onBlur={field.onBlur}
              />
            </FormControl>
          </FieldRow>
        </FormItem>
      )}
    />
  )
}
